import { useState } from 'react';
import { 
  Heart, 
  Utensils, 
  Church, 
  Clock, 
  Shirt, 
  Wallet,
  Sun,
  Moon,
  Coffee,
  MapPin,
  Globe,
  ArrowLeft,
  Download,
  Loader2,
  Map
} from "lucide-react";
import { Link } from "react-router-dom";
import { useLanguage } from "../context/LanguageContext";
import { useLocation } from "../context/LocationContext";
import { TopNav } from "../components/layout/TopNav";
import { MobileNav } from "../components/layout/MobileNav";
import { generatePocketGuide } from "../lib/pdfGenerator";
import { API } from "../lib/api";

export default function CulturalGuidePage() {
  const { t, language } = useLanguage();
  const { locationData } = useLocation();
  const [isGenerating, setIsGenerating] = useState(false);

  const etiquetteTips = [
    {
      icon: Heart,
      title: {
        en: "Greetings",
        it: "Saluti",
        fr: "Salutations",
        de: "Begrüßung",
        es: "Saludos"
      },
      tip: {
        en: "Italians often greet with a kiss on both cheeks. Start from the left. A handshake works for first meetings.",
        it: "Gli italiani spesso salutano con un bacio su entrambe le guance. Inizia da sinistra. Una stretta di mano va bene per i primi incontri.",
        fr: "Les Italiens se saluent souvent par un baiser sur les deux joues. Commencez par la gauche. Une poignée de main convient pour les premières rencontres.",
        de: "Italiener begrüßen sich oft mit einem Kuss auf beide Wangen. Beginnen Sie links. Ein Händedruck ist für erste Treffen angemessen.",
        es: "Los italianos suelen saludar con un beso en ambas mejillas. Empieza por la izquierda. Un apretón de manos es apropiado para los primeros encuentros."
      },
      color: "bg-pink-50 text-pink-600"
    },
    {
      icon: Utensils,
      title: {
        en: "Dining Hours",
        it: "Orari dei Pasti",
        fr: "Heures des Repas",
        de: "Essenszeiten",
        es: "Horarios de Comidas"
      },
      tip: {
        en: "Lunch is sacred (1-3pm). Many shops close. Dinner starts after 8pm. Arriving earlier may mean an empty restaurant.",
        it: "Il pranzo è sacro (13-15). Molti negozi chiudono. La cena inizia dopo le 20. Arrivare prima potrebbe significare un ristorante vuoto.",
        fr: "Le déjeuner est sacré (13h-15h). Beaucoup de magasins ferment. Le dîner commence après 20h. Arriver plus tôt peut signifier un restaurant vide.",
        de: "Das Mittagessen ist heilig (13-15 Uhr). Viele Geschäfte schließen. Abendessen beginnt nach 20 Uhr. Früher kommen kann ein leeres Restaurant bedeuten.",
        es: "El almuerzo es sagrado (13-15h). Muchas tiendas cierran. La cena comienza después de las 20h. Llegar antes puede significar un restaurante vacío."
      },
      color: "bg-amber-50 text-amber-600"
    },
    {
      icon: Church,
      title: {
        en: "Churches",
        it: "Chiese",
        fr: "Églises",
        de: "Kirchen",
        es: "Iglesias"
      },
      tip: {
        en: "Cover shoulders and knees when visiting religious sites. Carry a light scarf or cardigan. Photography may be restricted.",
        it: "Copri spalle e ginocchia quando visiti luoghi religiosi. Porta una sciarpa leggera o un cardigan. La fotografia potrebbe essere limitata.",
        fr: "Couvrez épaules et genoux lors de visites de sites religieux. Emportez un foulard léger ou un cardigan. La photographie peut être restreinte.",
        de: "Bedecken Sie Schultern und Knie beim Besuch religiöser Stätten. Tragen Sie einen leichten Schal oder Cardigan. Fotografieren kann eingeschränkt sein.",
        es: "Cubre hombros y rodillas al visitar lugares religiosos. Lleva un pañuelo ligero o un cardigan. La fotografía puede estar restringida."
      },
      color: "bg-purple-50 text-purple-600"
    },
    {
      icon: Clock,
      title: {
        en: "Siesta Time",
        it: "Tempo del Riposo",
        fr: "Heure de la Sieste",
        de: "Siesta-Zeit",
        es: "Hora de la Siesta"
      },
      tip: {
        en: "Respect the riposo (rest) from 1-4pm. Many businesses close. This is a great time for beach or a leisurely lunch.",
        it: "Rispetta il riposo dalle 13 alle 16. Molte attività chiudono. È un ottimo momento per la spiaggia o un pranzo tranquillo.",
        fr: "Respectez le riposo (repos) de 13h à 16h. Beaucoup de commerces ferment. C'est le moment idéal pour la plage ou un déjeuner tranquille.",
        de: "Respektieren Sie die Ruhezeit (Riposo) von 13-16 Uhr. Viele Geschäfte schließen. Ideal für Strand oder ein gemütliches Mittagessen.",
        es: "Respeta el riposo (descanso) de 13 a 16h. Muchos negocios cierran. Es un buen momento para la playa o un almuerzo tranquilo."
      },
      color: "bg-blue-50 text-blue-600"
    }
  ];

  const dressCodeTips = [
    {
      icon: Sun,
      title: {
        en: "Beach Attire",
        it: "Abbigliamento da Spiaggia",
        fr: "Tenue de Plage",
        de: "Strandkleidung",
        es: "Ropa de Playa"
      },
      tip: {
        en: "Beach attire is for the beach only. Cover up when visiting towns or entering shops. A sarong or light dress works well.",
        it: "L'abbigliamento da spiaggia è solo per la spiaggia. Copriti quando visiti i paesi o entri nei negozi. Un pareo o un vestito leggero vanno bene.",
        fr: "La tenue de plage est réservée à la plage. Couvrez-vous en ville ou dans les magasins. Un paréo ou une robe légère convient.",
        de: "Strandkleidung ist nur für den Strand. Bedecken Sie sich in Städten oder Geschäften. Ein Sarong oder leichtes Kleid funktioniert gut.",
        es: "La ropa de playa es solo para la playa. Cúbrete al visitar pueblos o entrar en tiendas. Un pareo o vestido ligero funciona bien."
      }
    },
    {
      icon: Utensils,
      title: {
        en: "Restaurant Dress",
        it: "Abbigliamento per Ristoranti",
        fr: "Tenue de Restaurant",
        de: "Restaurant-Kleidung",
        es: "Ropa para Restaurante"
      },
      tip: {
        en: "Smart casual for most restaurants. No shorts or flip-flops at upscale venues. Italians appreciate effort in appearance.",
        it: "Smart casual per la maggior parte dei ristoranti. Niente shorts o infradito nei locali eleganti. Gli italiani apprezzano la cura nell'aspetto.",
        fr: "Tenue décontractée chic pour la plupart des restaurants. Pas de shorts ni de tongs dans les établissements haut de gamme.",
        de: "Smart casual für die meisten Restaurants. Keine Shorts oder Flip-Flops in gehobenen Lokalen. Italiener schätzen gepflegtes Auftreten.",
        es: "Smart casual para la mayoría de restaurantes. Nada de shorts o chanclas en locales elegantes. Los italianos aprecian el esfuerzo en la apariencia."
      }
    },
    {
      icon: Moon,
      title: {
        en: "Evening Passeggiata",
        it: "Passeggiata Serale",
        fr: "Passeggiata du Soir",
        de: "Abend-Passeggiata",
        es: "Paseo Nocturno"
      },
      tip: {
        en: "Join the evening stroll! Pugliese dress elegantly for the passeggiata around 7-9pm. A great time for people-watching.",
        it: "Unisciti alla passeggiata serale! I pugliesi si vestono eleganti per la passeggiata intorno alle 19-21. Un ottimo momento per osservare la gente.",
        fr: "Rejoignez la promenade du soir! Les Pouillais s'habillent élégamment pour la passeggiata vers 19h-21h. Idéal pour observer les gens.",
        de: "Machen Sie mit beim Abendspaziergang! Apulier kleiden sich elegant für die Passeggiata gegen 19-21 Uhr. Ideal zum Leute beobachten.",
        es: "¡Únete al paseo nocturno! Los puglieses visten elegantes para la passeggiata entre las 19-21h. Un buen momento para observar a la gente."
      }
    }
  ];

  const tippingTips = [
    {
      icon: Utensils,
      title: {
        en: "Restaurants",
        it: "Ristoranti",
        fr: "Restaurants",
        de: "Restaurants",
        es: "Restaurantes"
      },
      tip: {
        en: "Service charge (coperto €1-3) is usually included. Round up if satisfied. 5-10% tip for exceptional service.",
        it: "Il servizio (coperto €1-3) è solitamente incluso. Arrotonda se soddisfatto. Mancia del 5-10% per un servizio eccezionale.",
        fr: "Le service (coperto €1-3) est généralement inclus. Arrondissez si satisfait. Pourboire de 5-10% pour un service exceptionnel.",
        de: "Servicegebühr (Coperto €1-3) ist meist enthalten. Bei Zufriedenheit aufrunden. 5-10% Trinkgeld für außergewöhnlichen Service.",
        es: "El servicio (coperto €1-3) suele estar incluido. Redondea si estás satisfecho. Propina del 5-10% por servicio excepcional."
      }
    },
    {
      icon: Coffee,
      title: {
        en: "Bars & Cafes",
        it: "Bar e Caffè",
        fr: "Bars et Cafés",
        de: "Bars & Cafés",
        es: "Bares y Cafés"
      },
      tip: {
        en: "Tipping is not expected. Leave €0.50-1 for table service. At the bar, Italians rarely tip.",
        it: "La mancia non è prevista. Lascia €0,50-1 per il servizio al tavolo. Al bancone, gli italiani raramente lasciano mancia.",
        fr: "Le pourboire n'est pas attendu. Laissez €0,50-1 pour le service à table. Au comptoir, les Italiens donnent rarement un pourboire.",
        de: "Trinkgeld ist nicht erwartet. Lassen Sie €0,50-1 für Tischservice. An der Bar geben Italiener selten Trinkgeld.",
        es: "No se espera propina. Deja €0,50-1 por servicio en mesa. En la barra, los italianos rara vez dan propina."
      }
    },
    {
      icon: MapPin,
      title: {
        en: "Tours & Guides",
        it: "Tour e Guide",
        fr: "Visites et Guides",
        de: "Touren & Guides",
        es: "Tours y Guías"
      },
      tip: {
        en: "€5-10 per person for excellent guides is appreciated. Boat tours and drivers also appreciate small tips.",
        it: "€5-10 a persona per guide eccellenti è apprezzato. Anche le guide delle barche e gli autisti apprezzano piccole mance.",
        fr: "€5-10 par personne pour d'excellents guides est apprécié. Les guides de bateaux et chauffeurs apprécient aussi les petits pourboires.",
        de: "€5-10 pro Person für ausgezeichnete Guides wird geschätzt. Bootsführer und Fahrer schätzen auch kleine Trinkgelder.",
        es: "€5-10 por persona para guías excelentes se agradece. Los guías de barcos y conductores también aprecian pequeñas propinas."
      }
    }
  ];

  const pageTitle = {
    en: "Puglia Culture Guide",
    it: "Guida alla Cultura Pugliese",
    fr: "Guide Culturel des Pouilles",
    de: "Apulien Kulturführer",
    es: "Guía Cultural de Puglia"
  };

  const pageSubtitle = {
    en: "Essential tips for respecting local traditions",
    it: "Consigli essenziali per rispettare le tradizioni locali",
    fr: "Conseils essentiels pour respecter les traditions locales",
    de: "Wichtige Tipps zur Wahrung lokaler Traditionen",
    es: "Consejos esenciales para respetar las tradiciones locales"
  };

  const sectionTitles = {
    etiquette: {
      en: "Local Etiquette",
      it: "Etichetta Locale",
      fr: "Étiquette Locale",
      de: "Lokale Etikette",
      es: "Etiqueta Local"
    },
    dressCode: {
      en: "Dress Code",
      it: "Codice di Abbigliamento",
      fr: "Code Vestimentaire",
      de: "Kleiderordnung",
      es: "Código de Vestimenta"
    },
    tipping: {
      en: "Tipping Guide",
      it: "Guida alle Mance",
      fr: "Guide des Pourboires",
      de: "Trinkgeld-Guide",
      es: "Guía de Propinas"
    }
  };

  return (
    <div className="min-h-screen bg-sand-200" data-testid="cultural-guide-page">
      <TopNav />

      <div className="max-w-4xl mx-auto px-4 py-8 pb-24 md:pb-8">
        {/* Back Button */}
        <Link 
          to="/" 
          className="inline-flex items-center gap-2 text-stone-600 hover:text-olive-700 mb-6 text-sm"
        >
          <ArrowLeft size={16} />
          <span>{language === 'it' ? 'Torna alla Home' : language === 'fr' ? 'Retour à l\'accueil' : language === 'de' ? 'Zurück zur Startseite' : language === 'es' ? 'Volver al Inicio' : 'Back to Home'}</span>
        </Link>

        {/* Header */}
        <div className="text-center mb-10">
          <div className="inline-flex items-center gap-2 bg-olive-100 text-olive-700 px-4 py-1.5 rounded-full text-sm font-medium mb-4">
            <Globe size={14} />
            <span>{language === 'it' ? 'Guida Culturale' : language === 'fr' ? 'Guide Culturel' : language === 'de' ? 'Kulturführer' : language === 'es' ? 'Guía Cultural' : 'Cultural Guide'}</span>
          </div>
          <h1 className="text-3xl md:text-4xl font-bold text-stone-800 mb-3">
            {pageTitle[language] || pageTitle.en}
          </h1>
          <p className="text-stone-500 text-lg">
            {pageSubtitle[language] || pageSubtitle.en}
          </p>
        </div>

        {/* Etiquette Section */}
        <section className="mb-10">
          <h2 className="text-xl font-semibold text-stone-800 mb-4 flex items-center gap-2">
            <Heart className="text-terracotta-500" size={20} />
            {sectionTitles.etiquette[language] || sectionTitles.etiquette.en}
          </h2>
          <div className="grid gap-4">
            {etiquetteTips.map((item, index) => (
              <div key={index} className="bg-white rounded-xl p-5 border border-sand-300 shadow-sm">
                <div className="flex items-start gap-4">
                  <div className={`p-2.5 rounded-lg ${item.color}`}>
                    <item.icon size={20} />
                  </div>
                  <div>
                    <h3 className="font-semibold text-stone-800 mb-1">
                      {item.title[language] || item.title.en}
                    </h3>
                    <p className="text-stone-600 text-sm leading-relaxed">
                      {item.tip[language] || item.tip.en}
                    </p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* Dress Code Section */}
        <section className="mb-10">
          <h2 className="text-xl font-semibold text-stone-800 mb-4 flex items-center gap-2">
            <Shirt className="text-sea-600" size={20} />
            {sectionTitles.dressCode[language] || sectionTitles.dressCode.en}
          </h2>
          <div className="bg-white rounded-xl border border-sand-300 shadow-sm divide-y divide-sand-200">
            {dressCodeTips.map((item, index) => (
              <div key={index} className="p-5">
                <div className="flex items-start gap-4">
                  <div className="p-2.5 rounded-lg bg-sand-200 text-stone-600">
                    <item.icon size={20} />
                  </div>
                  <div>
                    <h3 className="font-semibold text-stone-800 mb-1">
                      {item.title[language] || item.title.en}
                    </h3>
                    <p className="text-stone-600 text-sm leading-relaxed">
                      {item.tip[language] || item.tip.en}
                    </p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* Tipping Section */}
        <section className="mb-10">
          <h2 className="text-xl font-semibold text-stone-800 mb-4 flex items-center gap-2">
            <Wallet className="text-olive-600" size={20} />
            {sectionTitles.tipping[language] || sectionTitles.tipping.en}
          </h2>
          <div className="bg-gradient-to-br from-olive-100 to-sand-100 rounded-xl p-6 border border-olive-200">
            <div className="grid gap-5">
              {tippingTips.map((item, index) => (
                <div key={index} className="bg-white/80 backdrop-blur rounded-lg p-4">
                  <div className="flex items-start gap-3">
                    <div className="p-2 rounded-lg bg-olive-200 text-olive-700">
                      <item.icon size={18} />
                    </div>
                    <div>
                      <h3 className="font-semibold text-stone-800 mb-1 text-sm">
                        {item.title[language] || item.title.en}
                      </h3>
                      <p className="text-stone-600 text-sm">
                        {item.tip[language] || item.tip.en}
                      </p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Pro Tips */}
        <div className="bg-terracotta-100 rounded-xl p-5 border border-terracotta-200 mb-6">
          <h3 className="font-semibold text-terracotta-800 mb-2 flex items-center gap-2">
            <Sun size={18} />
            {language === 'it' ? 'Suggerimento Pro' : language === 'fr' ? 'Conseil Pro' : language === 'de' ? 'Profi-Tipp' : language === 'es' ? 'Consejo Pro' : 'Pro Tip'}
          </h3>
          <p className="text-terracotta-700 text-sm">
            {language === 'it' 
              ? 'Impara qualche parola italiana! "Buongiorno" (buon giorno), "Grazie" (grazie) e "Prego" (prego) sono molto apprezzate dai locali.'
              : language === 'fr'
              ? 'Apprenez quelques mots italiens! "Buongiorno" (bonjour), "Grazie" (merci) et "Prego" (je vous en prie) sont très appréciés des locaux.'
              : language === 'de'
              ? 'Lernen Sie ein paar italienische Wörter! "Buongiorno" (Guten Tag), "Grazie" (Danke) und "Prego" (Bitte) werden von Einheimischen sehr geschätzt.'
              : language === 'es'
              ? '¡Aprende algunas palabras italianas! "Buongiorno" (buenos días), "Grazie" (gracias) y "Prego" (de nada) son muy apreciadas por los locales.'
              : 'Learn a few Italian words! "Buongiorno" (good day), "Grazie" (thank you), and "Prego" (you\'re welcome) are greatly appreciated by locals.'}
          </p>
        </div>

        {/* Download & Offline Section */}
        <div className="grid md:grid-cols-2 gap-4">
          {/* Download Pocket Guide */}
          <button
            onClick={async () => {
              setIsGenerating(true);
              try {
                // Fetch beaches and events for the PDF
                const [beachesRes, eventsRes] = await Promise.all([
                  fetch(`${API}/beaches/curated`).then(r => r.json()).catch(() => []),
                  fetch(`${API}/events/enhanced`).then(r => r.json()).catch(() => [])
                ]);
                await generatePocketGuide(
                  language,
                  locationData?.name || 'Puglia',
                  beachesRes || [],
                  eventsRes || []
                );
              } catch (e) {
                console.error('PDF generation error:', e);
              } finally {
                setIsGenerating(false);
              }
            }}
            disabled={isGenerating}
            className="flex items-center justify-center gap-3 bg-teal-600 hover:bg-teal-700 text-white py-4 px-6 rounded-xl font-medium transition-colors disabled:opacity-50"
            data-testid="download-pocket-guide-btn"
          >
            {isGenerating ? (
              <>
                <Loader2 size={20} className="animate-spin" />
                <span>{language === 'it' ? 'Generazione...' : language === 'es' ? 'Generando...' : language === 'fr' ? 'Génération...' : language === 'de' ? 'Wird erstellt...' : 'Generating...'}</span>
              </>
            ) : (
              <>
                <Download size={20} />
                <span>{language === 'it' ? 'Scarica Guida PDF' : language === 'es' ? 'Descargar Guía PDF' : language === 'fr' ? 'Télécharger Guide PDF' : language === 'de' ? 'PDF-Guide herunterladen' : 'Download Pocket Guide'}</span>
              </>
            )}
          </button>

          {/* Offline Maps Link */}
          <Link
            to="/offline-maps"
            className="flex items-center justify-center gap-3 bg-stone-800 hover:bg-stone-900 text-white py-4 px-6 rounded-xl font-medium transition-colors"
            data-testid="offline-maps-link"
          >
            <Map size={20} />
            <span>{language === 'it' ? 'Mappe Offline' : language === 'es' ? 'Mapas Sin Conexión' : language === 'fr' ? 'Cartes Hors Ligne' : language === 'de' ? 'Offline-Karten' : 'Offline Maps'}</span>
          </Link>
        </div>
      </div>

      <MobileNav />
    </div>
  );
}
